import * as React from "react";
import {
    CnrDto,
    LanguageDto, OptionDto
} from "../../../../../services/Client";
import {
    ILocalColumnSizeSettings, IVirtualizedTableCellComponentProps,
    IVirtualizedTableColumn,
    TData
} from "../../../VirtualizedTableInterfaces";
import {Checkbox, Icon, Modal, Table} from "semantic-ui-react";
import {compose, Dispatch} from "redux";
import {connect} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {IReduxState} from "../../../../../redux/IReduxState";
import {
    selectCnrList,
    selectOptionTableSort,
    selectTypefamilyMap
} from "../../../../../redux/selectors";
import {translate} from "react-i18next";
import Button from "../../../../Button";
import OptionViewActions from "../../../../../redux/actions/OptionViewActions";
import {EditableValue} from "../../../../BasicDataGrid";
import {createMultiLineValues, extractMultiFields, getTypeFamilyName, getUniqueGenerations, OptionIndicatorColumns} from "./OptionIndicatorViewUtils";
import {Index, MultiGrid as ReactVirtualizedGrid} from "react-virtualized";
import {ITableSort} from "../../../../../utils/SortUtils";
import GeneralActions from "../../../../../redux/actions/GeneralActions";
import {makeSelectable, SelectableColumn} from "../../../../ColumnSelection";
import ColumnFactory from "../../../../../services/ColumnFactory";
import {buildComponent} from "../../../ComponentBuilder";
import _ from "lodash";
import {ITypefamilyMap} from "../../../../../redux/reducers/generalReducer";

interface IMapDispatchToProps {
    getOptionIndicators: (
        dto: OptionDto,
        setMessage: (message: string) => void,
        setResult: (optionIndicators: OptionDto[]) => void
    ) => void;
    setOptionTableSort: (sortObject: ITableSort) => void;
    dynTableSetSelectedIdList: (idList: string[]) => void;
}

interface IMapStateToProps {
    cnrList?: CnrDto[];
    typefamilyMap?: ITypefamilyMap;
    optionTableSort: ITableSort;
}

interface IOwnProps extends IVirtualizedTableCellComponentProps { }

interface IOptionIndicatorCellProps extends IMapStateToProps, IOwnProps, IMapDispatchToProps, RouteComponentProps<any> { }

interface IOptionIndicatorCellState {
    optionIndicators: OptionDto[];
    message: string;
    isOpen: boolean;
    optionColumns: SelectableColumn[];
    selectedItemId?: number;
    filterValue: {[key: string]: any};
    columnSizeSetting: ILocalColumnSizeSettings;
}

class OptionIndicatorCell extends React.Component<IOptionIndicatorCellProps, IOptionIndicatorCellState> {
    gridRef: ReactVirtualizedGrid | null;

    constructor(props: IOptionIndicatorCellProps) {
        super(props);
        this.state = {
            optionIndicators: [],
            message: "",
            isOpen: false,
            optionColumns: this.loadColumns(),
            selectedItemId: undefined,
            filterValue: {},
            columnSizeSetting: {},
        };
    }

    private setMessage = (message: string) => {
        this.setState({message});
    }

    private setResult = (result: OptionDto[]) => {
        this.setState({optionIndicators: result});
    }

    private onChange() {
        if (this.state.selectedItemId) {
            const selectedOptionDto = this.state.optionIndicators
                .find(optionIndicator => optionIndicator.objectId === this.state.selectedItemId);

            const dto = this.props.value.dto;

            const oldTexts = ColumnFactory.deepCopy(dto.texts);

            dto.texts = [];

            if (selectedOptionDto && selectedOptionDto.texts) {
                selectedOptionDto.texts.forEach(linkedOptionText => {
                    let oldText = _.find(oldTexts, optionText =>
                        optionText.language !== undefined && linkedOptionText.language !== undefined
                        && optionText.language.objectId === linkedOptionText.language.objectId);

                    if (!oldText) {
                        oldText = ColumnFactory.deepCopy(linkedOptionText);
                    } else {
                        oldText.shorttext = linkedOptionText.shorttext;
                        oldText.longtext = linkedOptionText.longtext;
                        oldText.footer = linkedOptionText.footer;
                        oldText.usp = linkedOptionText.usp;
                    }

                    dto.texts.push(oldText);
                });
            }

            const optionData = {
                objectId: dto.objectId,
                dto: dto,
            };

            this.props.onChange(optionData);
        }

        this.onClose();
    }

    private onClose() {
        this.setState({
            optionIndicators: [],
            selectedItemId: undefined,
            message: "",
            isOpen: false,
        });
    }

    private openModal() {
        this.setState({
            isOpen: true,
        });
    }

    private loadColumns() {
        return OptionIndicatorColumns.map(
            column => makeSelectable(
                OptionIndicatorCell.createColumn(column.key), true));
    }

    private static createColumn(key: string) {
        return ColumnFactory.getColumnWithParameters(key, {keyEditable: EditableValue.Readonly,});
    }

    private onChangeRowSelection(checked: boolean, id: number) {
        if (checked && id) {
            this.setState({
                selectedItemId: id,
            });
        } else {
            this.setState({
                selectedItemId: undefined,
            });
        }
    }

    private getColumnWidth({index}: Index): number {
        const {columnSizeSetting} = this.state;

        const iVirtualizedTableColumn = OptionIndicatorColumns[index];

        if (columnSizeSetting[iVirtualizedTableColumn.key]) {
            return columnSizeSetting[iVirtualizedTableColumn.key].width + 20;
        }

        return iVirtualizedTableColumn && iVirtualizedTableColumn.options && iVirtualizedTableColumn.options.cellWidth
            ? iVirtualizedTableColumn.options.cellWidth : 200;
    }

    private renderHeaderRow(t:reactI18Next.TranslationFunction): React.ReactNode {
        return (
            <Table.Row>
                <Table.HeaderCell
                    key={0}
                    style={{zIndex: 10}}
                    className={'grid-header-cell grid-first-column-row option-indicator-table-header-cell'}
                >
                    <div className='grid-first-column-row'>{}</div>
                </Table.HeaderCell>
                {
                    OptionIndicatorColumns.map((column, idx) => {
                        return this.renderHeaderCell(column, idx, t);
                    })
                }
            </Table.Row>
        );
    }

    private renderHeaderCell(column:IVirtualizedTableColumn, idx:number, t:reactI18Next.TranslationFunction): React.ReactNode {
        return (
            <Table.HeaderCell key={idx + 1} className={'grid-header-cell option-indicator-table-header-cell'}>
                <div
                    style={{width: this.getColumnWidth({index: idx}) + 'px'}}
                >
                    {t(`COLUMN_FACTORY:${column.label}`)}
                </div>
            </Table.HeaderCell>
        );
    }

    private renderOptionGridBodyRow(item:TData, rowIndex:number): React.ReactNode {
        return (
            <Table.Row key={rowIndex} className={`${rowIndex % 2 === 0 ? 'grid-white-row' : 'grid-grey-row'}`}>
                <Table.Cell key={0}>
                    <div
                        className={'grid-centered-cell'}
                    >
                        <Checkbox
                            slider={true}
                            checked={true}
                            readOnly={true}
                        />
                    </div>
                </Table.Cell>
                {
                    OptionIndicatorColumns.map((column, idx) => {
                        return this.renderGridBodyCell(rowIndex, idx, item, column);
                    })
                }
            </Table.Row>
        );
    }

    private renderGridBodyRow(item:TData, rowIndex:number): React.ReactNode {
        return (
            <Table.Row key={rowIndex} className={`${rowIndex % 2 === 0 ? 'grid-white-row' : 'grid-grey-row'}`}>
                <Table.Cell key={0}>
                    <div
                        className={'grid-centered-cell'}
                    >
                        <Checkbox
                            slider={true}
                            checked={item.objectId === this.state.selectedItemId}
                            onChange={(e, v) => {
                                this.onChangeRowSelection(v.checked as boolean, item.objectId as number);
                            }}
                        />
                    </div>
                </Table.Cell>
                {
                    OptionIndicatorColumns.map((column, idx) => {
                        return this.renderGridBodyCell(rowIndex, idx, item, column);
                    })
                }
            </Table.Row>
        );
    }

    /**
     *  Computes the cells of the Grid body
     */
    private renderGridBodyCell = (
        rowIndex: number,
        columnIndex: number,
        item: TData,
        visibleColumn: IVirtualizedTableColumn,
    ): React.ReactNode => {
        const rowClass: string = `${this.props.isSpecificPSL
            ? item.rowClass
                ? item.rowClass : 'row-BASIC'
            : ''}`;

        const key: string = visibleColumn.key;
        const TargetComponent = buildComponent(visibleColumn.rowType);

        return (
            <Table.Cell key={columnIndex + 1}>
                <div
                    className={rowClass}
                    key={`${rowIndex}:${columnIndex}`}
                >
                    <TargetComponent
                        value={item[key]}
                        onChange={(newValue) => this.props.onChange(newValue)}
                        readOnly={true}
                        uglyMode={false}
                        rowIndex={rowIndex}
                        t={this.props.t}
                        language={this.props.language}
                        inEditMode={false}
                        isSpecificPSL={this.props.isSpecificPSL}
                        isIndicator={true}
                    />
                </div>
            </Table.Cell>
        );
    }

    render() {
        const {t, value, cnrList, typefamilyMap} = this.props;

        const modelYear = value.dto.optionBasic.modelyear;
        const typeFamilyName = getTypeFamilyName(value.dto.typefamilyId, typefamilyMap, modelYear);
        const cnr = cnrList ? _.find(cnrList, item => item.objectId === value.dto.cnrId) : undefined;
        const optionData: TData[] = [{
            objectId: value.dto.objectId,
            categoryText: value.dto.optionBasic.categoryText,
            cnr: value.dto.cnrId,
            typeFamily: typeFamilyName,
            optionBasicView: value.dto.optionBasic.optionCode,
            shorttext: extractMultiFields(createMultiLineValues(value.dto.texts, "shorttext", value.selectedLanguages)),
            longtext: extractMultiFields(createMultiLineValues(value.dto.texts, "longtext", value.selectedLanguages)),
            footer: extractMultiFields(createMultiLineValues(value.dto.texts, "footer", value.selectedLanguages)),
            usp: extractMultiFields(createMultiLineValues(value.dto.texts, "usp", value.selectedLanguages)),
            generation: getUniqueGenerations(
                value.dto.modeltypes && value.dto.modeltypes
                    .map((modelType: any) => modelType.modeltypeBasic)
                    .filter((mt: any) => mt && mt.generation)
                    .map((mt: any) => mt.generation.generation).join(', ')
            ),
        }];

        return this.props.value.optionCount === undefined || this.props.value.optionCount === 0
            ?
            (
                <div className='option-indicator-cell-total-count'>
                    <span>{0}</span>
                    <Icon name='sitemap' />
                </div>
            )
            :
            (
                <Modal
                    closeIcon={true}
                    className="option-indicator-dialog-container"
                    open={this.state.isOpen}
                    size={"fullscreen"}
                    trigger={
                        <div className='option-indicator-cell-total-count' style={{cursor: "pointer"}} onClick={() => this.openModal()}>
                            <span>{value.optionCount}</span>
                            <Icon name='sitemap' />
                        </div>}
                    onOpen={() => this.props.getOptionIndicators(value.dto, this.setMessage, this.setResult)}
                    onClose={() => this.onClose()}
                >
                    <Modal.Content>
                        <div className="option-container">
                            <p
                                style={{
                                    fontSize: 16,
                                    fontWeight: "bold",
                                }}
                            >
                                {`${t("WORKFLOW:OPTION")} /
                                ${t("GLOBAL:MODELYEAR")}: ${modelYear.mj1} (${modelYear.mj4}) -
                                ${t("GLOBAL:TYPEFAMILY")}: ${typeFamilyName} -
                                ${t("GLOBAL:CNR")}: ${cnr ? cnr.name : ''} -
                                ${t("COLUMN_FACTORY:OPTIONCODE")}: ${value.dto.optionBasic.optionCode}`}
                            </p>
                            <div className="option-table-container">
                                <Table
                                    id="optionTable"
                                    textAlign='left'
                                    verticalAlign='middle'
                                    className="ui striped table"
                                    celled={true}
                                >
                                    <Table.Header>
                                        {this.renderHeaderRow(t)}
                                    </Table.Header>
                                    <Table.Body>
                                        {optionData.map((option, idx) => {
                                            return this.renderOptionGridBodyRow(option, idx);
                                        })}
                                    </Table.Body>
                                </Table>
                            </div>
                        </div>

                        <div className="option-indicator-container">
                            <p
                                style={{
                                    fontSize: 16,
                                    fontWeight: "bold",
                                }}
                            >
                                {t("OPTIONS_VIEW:CROSS_LINKED_OPTIONS")}
                            </p>
                            <div className="option-indicator-table-container">
                                <Table
                                    id="optionIndicatorTable"
                                    textAlign='left'
                                    verticalAlign='middle'
                                    className="ui striped table"
                                    celled={true}
                                >
                                    <Table.Header>
                                        {this.renderHeaderRow(t)}
                                    </Table.Header>
                                    <Table.Body>
                                        {this.state.optionIndicators.length !== 0
                                            && this.state.optionIndicators.map((dto: OptionDto): any => {
                                                return {
                                                    objectId: dto.objectId,
                                                    categoryText: dto.optionBasic.categoryText,
                                                    cnr: dto.cnrId,
                                                    typeFamily: getTypeFamilyName(dto.typefamilyId, typefamilyMap, dto.optionBasic.modelyear),
                                                    optionBasicView: dto.optionBasic.optionCode,
                                                    shorttext: extractMultiFields(
                                                        createMultiLineValues(dto.texts, "shorttext", value.selectedLanguages)),
                                                    longtext: extractMultiFields(
                                                        createMultiLineValues(dto.texts, "longtext", value.selectedLanguages)),
                                                    footer: extractMultiFields(
                                                        createMultiLineValues(dto.texts, "footer", value.selectedLanguages)),
                                                    usp: extractMultiFields(
                                                        createMultiLineValues(dto.texts, "usp", value.selectedLanguages)),
                                                    generation: getUniqueGenerations(
                                                        dto.modeltypes && dto.modeltypes
                                                            .map((modelType: any) => modelType.modeltypeBasic)
                                                            .filter((mt: any) => mt && mt.generation)
                                                            .map((mt: any) => mt.generation.generation).join(', ')
                                                    ),
                                                };
                                            }).map((option, idx) => {
                                                return this.renderGridBodyRow(option, idx);
                                            })}
                                    </Table.Body>
                                </Table>
                            </div>
                        </div>
                    </Modal.Content>

                    <Modal.Actions>
                        {
                            [
                                <Button
                                    key={"copyButton"}
                                    label={this.props.t("OPTIONS_VIEW:TAKE_OVER_OPTIONINDICATOR_TEXT")}
                                    icon={"copy"}
                                    className='width--100Percent'
                                    style={{margin: '10px 0 0'}}
                                    disabled={!this.props.inEditMode || this.props.readOnly || this.state.selectedItemId === undefined}
                                    onClick={() => this.onChange()}
                                />
                            ]
                        }
                    </Modal.Actions>
                </Modal>
            );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>, ownProps: IOptionIndicatorCellProps): IMapDispatchToProps => {
    return {
        getOptionIndicators: (dto, setMessage, setResult) =>
            dispatch(OptionViewActions.GetOptionIndicators(ownProps.t, dto, setMessage, setResult)),
        setOptionTableSort: (sortObject) => dispatch(OptionViewActions.SetOptionTableSort(sortObject)),
        dynTableSetSelectedIdList: (idList) => dispatch(GeneralActions.DynTableSetSelectedIdList(idList)),
    };
};

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    cnrList: selectCnrList(state),
    typefamilyMap: selectTypefamilyMap(state),
    optionTableSort: selectOptionTableSort(state),
});

export default compose<React.ComponentType<IOwnProps>>(
    withRouter,
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(OptionIndicatorCell);



// WEBPACK FOOTER //
// ./src/components/Table/components/basic/optionIndicator/OptionIndicatorCell.tsx